// src/pages/functions/FunctionAssistedLivingDetailPage.js
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import ProductGrid from "../../components/category_page/ProductGrid";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

export default function FunctionAssistedLivingDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const goToFilteredProducts = () =>
    navigate("/kategoriak?all=1&functions=asszisztalt-elet");

  const goToBuilder = () =>
    navigate("/builder", { state: { functionIds: ["asszisztalt-elet"] } });

  return (
    <motion.div
      key={location.key}
      className="min-h-screen bg-gradient-to-b from-white to-slate-50 text-[#0c1a26]"
      initial="hidden"
      animate="show"
    >
      {/* ===== HERO – egységes stílus (Home/Builder) ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c1a26] to-[#173047]" />
        <div className="absolute inset-0 opacity-25">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url(${"/img/t5.webp"})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-20 md:pb-28 text-white">
          <motion.h1 className="text-3xl md:text-5xl font-bold drop-shadow-sm" variants={fadeUp}>
            Asszisztált életvitel
          </motion.h1>
          <motion.p className="text-white/90 text-lg md:text-xl max-w-3xl mt-2" variants={fadeUp}>
            Gondoskodó automatizmusok, pánikjelzés és diszkrét felügyelet – hogy a
            mindennapok <strong>biztonságosabban és önállóbban</strong> teljenek.
          </motion.p>

          <motion.div className="mt-7 flex flex-wrap gap-3" variants={fadeUp}>
            <button
              onClick={goToFilteredProducts}
              className="inline-flex items-center justify-center rounded-xl bg-blue-600 hover:bg-blue-700 px-5 py-3 font-semibold"
            >
              Termékek (előszűrve) →
            </button>
            <button
              onClick={goToBuilder}
              className="inline-flex items-center justify-center rounded-xl border border-white/40 hover:bg-white hover:text-[#0c1a26] px-5 py-3 font-semibold"
            >
              Kezd el a Builderben
            </button>
            <button
              onClick={() => navigate("/funkciok")}
              className="inline-flex items-center justify-center rounded-xl border border-white/40/60 hover:bg-white/10 px-5 py-3 font-semibold"
            >
              Vissza a funkciókhoz
            </button>
          </motion.div>
        </div>
      </section>

      {/* ===== TARTALOM ===== */}
      <div className="max-w-7xl mx-auto px-6 py-10 space-y-14">

        {/* Bevezető + fő kép (a korábbi oldalról) */}
        <section className="grid md:grid-cols-[1.3fr_1fr] gap-6 items-stretch">
          <motion.div
            className="bg-white/90 backdrop-blur rounded-2xl border p-5 md:p-6"
            variants={fadeUp}
          >
            <h2 className="text-2xl font-semibold">Mi ez?</h2>
            <p className="text-gray-700 mt-2">
              Az asszisztált életvitel célja, hogy <strong>észrevétlenül segítsen</strong> a
              mindennapokban: finom automatizmusokkal, szükség esetén pedig azonnali
              jelzéssel a családnak vagy gondozónak.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Gyors segítséghívás</div>
                <div className="text-sm text-gray-700">Pánikgomb, esésérzékelés, automatikus jelzés.</div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Diszkrét felügyelet</div>
                <div className="text-sm text-gray-700">Szokatlan inaktivitás észlelése, figyelmeztetés.</div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Kényelmi rutinok</div>
                <div className="text-sm text-gray-700">Világítás, hőmérséklet, emlékeztetők, fényösvény.</div>
              </div>
              <div className="rounded-xl border bg-gray-50 p-3">
                <div className="font-medium">Családi értesítések</div>
                <div className="text-sm text-gray-700">Megnyugtató visszajelzés a távolból.</div>
              </div>
            </div>
          </motion.div>

          {/* Fő kép – /img/base/asszisztalt-elet.webp */}
          <motion.figure
            className="rounded-2xl border overflow-hidden bg-white"
            variants={fadeUp}
          >
            <img
              src="/img/base/asszisztalt-elet.webp"
              alt="Asszisztált életvitel – fő illusztráció"
              className="w-full h-full object-contain"
              loading="lazy"
            />
            <figcaption className="px-4 py-2 text-xs text-gray-600">
              Asszisztált életvitel – gondoskodó, finom automatizmusok.
            </figcaption>
          </motion.figure>
        </section>

        {/* Videók – rövid MP4-ek, loop + leírás */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-3" variants={fadeUp}>
            Hogyan néz ki működés közben?
          </motion.h2>
          <p className="text-sm text-gray-600 mb-4">
            Rövid demók: biztonságos éjszakai közlekedés és azonnali segítséghívás – a videók halkan, végtelenítve futnak.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Video #1 */}
            <motion.figure
              className="rounded-2xl overflow-hidden border bg-black"
              variants={fadeUp}
            >
              <video
                className="w-full h-auto"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src="/videos/asszisztalt-eletvitel-1.mp4" type="video/mp4" />
              </video>
              <figcaption className="p-3 bg-white text-sm text-gray-700">
                <strong>Biztonságos éjszakai közlekedés:</strong> finom vezetőfény és
                <em> automatikusan felkapcsoló útvonal</em> – kevesebb botlás, több nyugalom.
              </figcaption>
            </motion.figure>

            {/* Video #2 */}
            <motion.figure
              className="rounded-2xl overflow-hidden border bg-black"
              variants={fadeUp}
            >
              <video
                className="w-full h-auto"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src="/videos/asszisztalt-eletvitel-2.mp4" type="video/mp4" />
              </video>
              <figcaption className="p-3 bg-white text-sm text-gray-700">
                <strong>Azonnali segítség:</strong> eltérésnél vagy baj gyanúja esetén
                <em> automatikus értesítés</em> a családnak/gondozónak.
              </figcaption>
            </motion.figure>
          </div>
        </section>

        {/* Példa automatizmusok */}
        <section className="grid md:grid-cols-3 gap-6">
          {[
            { title: "Éjszakai fényösvény", desc: "Mozgásra padlószintű fény, majd lassú elhalványulás." },
            { title: "Inaktivitás-figyelés", desc: "Szokatlan csend esetén értesítés – először csendes, majd hangsúlyosabb." },
            { title: "Pánikgomb / esésérzékelés", desc: "Azonnali jelzés + ajtónyitás/megvilágítás a segítségnek." },
          ].map((a, i) => (
            <motion.div
              key={a.title}
              className="bg-white border rounded-xl p-4 shadow-sm"
              variants={fadeUp}
              custom={i}
              whileHover={{ y: -3, boxShadow: "0 8px 20px rgba(12,26,38,0.08)" }}
              transition={{ type: "spring", stiffness: 200, damping: 18 }}
            >
              <h3 className="font-semibold">{a.title}</h3>
              <p className="text-sm text-gray-700 mt-1">{a.desc}</p>
            </motion.div>
          ))}
        </section>

        {/* Ajánlott termékek – funkció szerint előszűrve */}
        <section>
          <motion.h2 className="text-2xl font-semibold mb-4" variants={fadeUp}>
            Ajánlott termékek
          </motion.h2>
          <div className="rounded-2xl border bg-white/80 backdrop-blur p-4 sm:p-5 shadow-sm">
            <ProductGrid
              activeFilters={{ functionId: "asszisztalt-elet" }}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={goToFilteredProducts}
              className="rounded-xl bg-blue-600 text-white px-5 py-3 hover:bg-blue-700"
            >
              Termékek teljes listája →
            </button>
            <button
              onClick={goToBuilder}
              className="rounded-xl border px-5 py-3 hover:bg-gray-50"
            >
              Asszisztált életvitel építése a Builderben
            </button>
          </div>
        </section>
      </div>

      {/* Sticky mobil CTA */}
      <div className="md:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40">
        <motion.button
          className="bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg"
          onClick={goToFilteredProducts}
          aria-label="Ugrás az asszisztált életvitel termékekhez"
          whileTap={{ scale: 0.98 }}
          whileHover={{ scale: 1.02 }}
        >
          Kapcsolódó termékek →
        </motion.button>
      </div>
    </motion.div>
  );
}
